import { Link } from 'react-router-dom';
import { BrandMark } from '../components/BrandMark';

/**
 * The legal minimum for a public app (PLAN.md §4): what is stored, what is not, and the Nexon
 * disclaimer. Deliberately outside `ProtectedLayout` — a privacy notice you have to log in to read
 * is not a privacy notice — so it carries its own lightweight chrome instead of the AppShell.
 *
 * Voice per docs/DESIGN.md: plain and warm, no legalese theatre. Everything here is a statement of
 * fact about the code in this repo; if the code changes, this page changes with it.
 */

/**
 * PRE-LAUNCH: confirm this mailbox exists and is monitored before the domain goes public — it is
 * the only route a user has to request deletion of their data. Tracked in deploy/RUNBOOK.md §9.
 */
const CONTACT_EMAIL = 'contact@rizkybusiness.dev';

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="space-y-2">
      <h2 className="font-display text-base font-semibold text-text">{title}</h2>
      <div className="space-y-2 text-sm leading-relaxed text-text-muted">{children}</div>
    </section>
  );
}

export function Privacy() {
  return (
    <div
      className="shell-grain min-h-full bg-bg text-text"
      style={{
        backgroundImage:
          'radial-gradient(720px 520px at 75% -10%, var(--shell-glow), transparent 70%)',
      }}
    >
      <header className="border-b border-border bg-bg-elevated/90 backdrop-blur-md">
        <div className="flex min-h-tap w-full items-center justify-between gap-4 px-4 py-2 sm:px-6 lg:px-8">
          <Link to="/" className="inline-flex items-center rounded-lg focus-visible:outline-none">
            <BrandMark />
          </Link>
          <Link
            to="/"
            className="inline-flex min-h-tap items-center px-2 text-sm font-semibold text-text-muted transition-colors hover:text-text"
          >
            Back to the app
          </Link>
        </div>
      </header>

      {/* Full-width pane like the rest of the app (docs/DESIGN.md "Layout") — the ONLY width cap
          in the app lives here, on the prose itself (`max-w-prose`), left-aligned within the pane
          rather than centering the whole page. */}
      <main id="main-content" className="w-full px-4 py-8 sm:px-6 lg:px-8">
        <h1 className="font-display text-2xl font-semibold text-text">Privacy, plainly</h1>
        <p className="mt-2 max-w-prose text-sm text-text-muted">
          HyperMaple is a hobby tracker run by one person on one small server. Here is everything it
          knows about you.
        </p>

        <div className="mt-8 max-w-prose space-y-8">
          <Section title="What signing in gives us">
            <p>
              Sign-in is Discord and only Discord — there is no password field anywhere in this app,
              so there is no password of yours to leak. We ask Discord for the{' '}
              <code className="text-text">identify</code> scope, which returns three things: your
              Discord user id, your username, and your avatar hash. We store those so the app can
              greet you and show your avatar. We do not ask for your email, your servers, or
              anything else, and Discord will not give it to us if we did not ask.
            </p>
          </Section>

          <Section title="What you type in">
            <p>
              Your characters, their levels and classes, your task and boss check-offs, and your
              long-term goals. All of it is entered by hand — HyperMaple never contacts Nexon or any
              third-party game API, so nothing about your account is fetched behind your back.
            </p>
            <p>
              Your data is keyed to your user and readable only by you. There is no sharing feature,
              no public profile, and no leaderboard.
            </p>
            <p>
              &ldquo;Import from screenshot&rdquo; reads character cards out of a Switch Character
              screenshot you choose, entirely inside your browser. The image is never uploaded
              anywhere — it is not sent to our server, and no OCR runs on any server we operate.
              You review and confirm every detected character before anything is saved, exactly
              like typing it in by hand.
            </p>
          </Section>

          <Section title="Cookies and tracking">
            <p>
              One cookie: the session cookie that keeps you logged in. It is{' '}
              <span className="text-text">HttpOnly</span>,{' '}
              <span className="text-text">Secure</span> and{' '}
              <span className="text-text">SameSite=Lax</span>, and it holds nothing but a session
              identifier.
            </p>
            <p>
              No analytics, no advertising pixels, no fingerprinting, no third-party scripts of any
              kind. Your accent-colour preference lives in your own browser&apos;s local storage and
              never reaches the server. Server logs keep ordinary request lines (time, path, status)
              for operational debugging.
            </p>
          </Section>

          <Section title="Deleting your data">
            <p>
              Email{' '}
              <a
                href={`mailto:${CONTACT_EMAIL}?subject=HyperMaple%20data%20deletion`}
                className="text-accent underline decoration-accent-border underline-offset-2 hover:decoration-accent"
              >
                {CONTACT_EMAIL}
              </a>{' '}
              with your Discord username and everything tied to your account is removed from the
              database and from the next night&apos;s backup rotation. No hoops, no retention
              period. The same address is the place to ask anything else about this page.
            </p>
          </Section>

          <Section title="Not affiliated with Nexon">
            <p>
              HyperMaple is an unofficial, fan-made tool. It is not affiliated with, endorsed by, or
              sponsored by Nexon Korea Corporation or NEXON America Inc. MapleStory and all related
              names and imagery are trademarks of their respective owners; none of Nexon&apos;s art,
              fonts or game assets are used here. Game numbers are transcribed by hand from public
              patch notes and community sources, are versioned per patch, and can be wrong — the app
              marks the values it has not verified.
            </p>
          </Section>
        </div>

        <p className="mt-10 max-w-prose border-t border-border pt-4 text-xs text-text-faint">
          If this page ever falls out of step with what the app actually does, the app is wrong and
          it gets fixed. Say so at {CONTACT_EMAIL}.
        </p>
      </main>
    </div>
  );
}
